import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Building2, MapPin, Edit, Trash2, Plus, Loader2, Users, Bed, Bath, Settings, DollarSign } from 'lucide-react';

const PropertiesPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProperties = async () => {
    try {
      const resp = await propertiesAPI.list();
      setProperties(resp.data);
    } catch (err) {
      console.error('Error loading properties:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProperties(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property? All related bookings will also be removed.')) return;
    try {
      await propertiesAPI.delete(id);
      loadProperties();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" /></div>;
  }

  return (
    <div data-testid="properties-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-airbnb-gray">Properties</h1>
          <p className="text-airbnb-medium-gray mt-1">Manage your rental properties</p>
        </div>
        <Button data-testid="add-property-btn" onClick={() => navigate('/properties/new/setup')} className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> Add Property
        </Button>
      </div>

      {properties.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
          <Building2 className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <p className="text-airbnb-medium-gray text-lg mb-2">No properties yet</p>
          <Button onClick={() => navigate('/properties/new/setup')} className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl">Add Your First Property</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {properties.map((prop) => (
            <div key={prop.property_id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`property-card-${prop.property_id}`}>
              <div className="h-44 bg-gray-100 relative overflow-hidden cursor-pointer" onClick={() => navigate(`/properties/${prop.property_id}/setup`)}>
                {prop.image_url ? (
                  <img src={prop.image_url} alt={prop.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300">
                    <Building2 className="w-14 h-14" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <span className="bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-medium text-airbnb-gray capitalize shadow-sm">
                    {prop.property_type}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-airbnb-gray text-lg mb-1 truncate">{prop.name}</h3>
                {(prop.city || prop.country) && (
                  <div className="flex items-center text-sm text-airbnb-medium-gray mb-1">
                    <MapPin className="w-3.5 h-3.5 mr-1 text-airbnb-pink" />
                    <span className="truncate">{[prop.neighborhood, prop.city, prop.state, prop.country].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {prop.short_description && (
                  <p className="text-xs text-airbnb-medium-gray mb-3 line-clamp-2">{prop.short_description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-airbnb-medium-gray mb-3">
                  <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{prop.bedrooms} bed</span>
                  <span className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{prop.bathrooms} bath</span>
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{prop.max_guests} guests</span>
                </div>
                {(prop.cleaning_fee > 0 || (prop.amenities?.length || 0) > 0) && (
                  <div className="flex items-center gap-2 text-xs text-airbnb-medium-gray mb-3">
                    {prop.cleaning_fee > 0 && <span className="bg-gray-100 px-2 py-0.5 rounded-full">+${prop.cleaning_fee} cleaning</span>}
                    {(prop.amenities?.length || 0) > 0 && <span className="bg-gray-100 px-2 py-0.5 rounded-full">{prop.amenities.length} amenities</span>}
                  </div>
                )}
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                  <span className="text-lg font-bold text-airbnb-pink"><DollarSign className="w-4 h-4 inline" />{prop.base_price}<span className="text-xs font-normal text-airbnb-medium-gray">/night</span></span>
                  <div className="flex gap-1">
                    <Button data-testid={`setup-property-${prop.property_id}`} variant="ghost" size="sm" onClick={() => navigate(`/properties/${prop.property_id}/setup`)} className="h-8 px-2 text-xs text-airbnb-medium-gray hover:text-airbnb-pink hover:bg-airbnb-light-pink">
                      <Settings className="w-3.5 h-3.5 mr-1" /> Setup
                    </Button>
                    <Button data-testid={`delete-property-${prop.property_id}`} variant="ghost" size="sm" onClick={() => handleDelete(prop.property_id)} className="h-8 w-8 p-0 text-airbnb-medium-gray hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;
